import { Injectable } from '@angular/core';
import {
    ActionBarContext,
    addActionBarItem,
    DataService,
    ModalService,
    NotificationService,
    Promotion,
} from '@vendure/admin-ui/core';
import gql from 'graphql-tag';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { RejectReasonDialogComponent } from './reject-reason-dialog/reject-reason-dialog.component';

interface AcceptPromotion {
    acceptPromotion: Promotion;
}
interface RejectPromotion {
    rejectPromotion: Promotion;
}

@Injectable()
class MyService {
    constructor(
        private readonly dataService: DataService,
        private router: Router,
        private modalService: ModalService,
    ) {}

    acceptPromotion(id: string): Observable<AcceptPromotion> {
        const mutation = gql`
            mutation AcceptPromotion($id: ID!) {
                acceptPromotion(id: $id) {
                    id
                }
            }
        `;
        return this.dataService
            .mutate<AcceptPromotion>(mutation, {
                id,
            })
            .pipe(
                catchError(error => {
                    throw error;
                }),
            );
    }

    rejectPromotion(id: string, reason: string): Observable<RejectPromotion> {
        const mutation = gql`
            mutation RejectPromotion($id: ID!, $reason: String!) {
                rejectPromotion(id: $id, reason: $reason) {
                    id
                }
            }
        `;
        return this.dataService
            .mutate<RejectPromotion>(mutation, {
                id,
                reason,
            })
            .pipe(
                catchError(error => {
                    throw error;
                }),
            );
    }
}
export default [
    MyService,
    addActionBarItem({
        id: 'accept-promotion',
        locationId: 'promotion-detail',
        label: 'Accept Promotion',
        icon: 'check',
        buttonColor: 'success',
        buttonState: (context: ActionBarContext) => {
            const query = gql`
                query CanAcceptPromotion($id: ID!) {
                    canAcceptPromotion(id: $id)
                }
            `;
            return context.dataService
                .query(query, {
                    id: context.route.snapshot.params.id,
                })
                .mapSingle(response => (response as any).canAcceptPromotion)
                .pipe(
                    map(resp => {
                        return {
                            disabled: !resp,
                            visible: resp,
                        };
                    }),
                );
        },
        onClick: async (event: MouseEvent, context: ActionBarContext) => {
            const modalService = context.injector.get(ModalService);
            modalService
                .dialog({
                    title: 'Are you sure you want to accept this promotion?',
                    buttons: [
                        { type: 'secondary', label: _('common.cancel') },
                        { type: 'primary', label: _('common.confirm'), returnValue: true },
                    ],
                })
                .subscribe(async confirmed => {
                    if (confirmed) {
                        const id = context.route.snapshot.params.id;
                        const myService = context.injector.get(MyService);
                        const notificationService = context.injector.get(NotificationService);
                        myService.acceptPromotion(id).subscribe({
                            next: response => {
                                const thePromo = response.acceptPromotion;
                                if (thePromo) {
                                    notificationService.success(`Promotion accepted successfully.`);
                                    setTimeout(() => {
                                        window.location.reload();
                                    }, 500);
                                } else {
                                    notificationService.error(
                                        `Error accepting the Promotion. Please contact support.`,
                                    );
                                }
                            },
                            error: () => {
                                notificationService.error(
                                    'Error accepting the Promotion. Please contact support.',
                                );
                            },
                        });
                    }
                });
        },
    }),
    addActionBarItem({
        id: 'reject-promotion',
        locationId: 'promotion-detail',
        label: 'Reject Promotion',
        icon: 'danger',
        buttonColor: 'warning',
        buttonState: (context: ActionBarContext) => {
            const query = gql`
                query CanRejectPromotion($id: ID!) {
                    canRejectPromotion(id: $id)
                }
            `;
            return context.dataService
                .query(query, {
                    id: context.route.snapshot.params.id,
                })
                .mapSingle(response => (response as any).canRejectPromotion)
                .pipe(
                    map(resp => {
                        return {
                            disabled: !resp,
                            visible: resp,
                        };
                    }),
                );
        },
        onClick: async (event: MouseEvent, context: ActionBarContext) => {
            const modalService = context.injector.get(ModalService);
            modalService
                .fromComponent(RejectReasonDialogComponent, {
                    size: 'md',
                    closable: true,
                })
                .subscribe(result => {
                    if (result.response) {
                        const id = context.route.snapshot.params.id;
                        const myService = context.injector.get(MyService);
                        const notificationService = context.injector.get(NotificationService);
                        myService.rejectPromotion(id, result.response).subscribe({
                            next: response => {
                                const thePromo = response.rejectPromotion;
                                if (thePromo) {
                                    notificationService.success(`Promotion rejected successfully.`);
                                    setTimeout(() => {
                                        window.location.reload();
                                    }, 500);
                                } else {
                                    notificationService.error(
                                        `Error rejecting the Promotion. Please contact support.`,
                                    );
                                }
                            },
                            error: () => {
                                notificationService.error(
                                    'Error rejecting the Promotion. Please contact support.',
                                );
                            },
                        });
                    }
                });
        },
    }),
];
